import React, { useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import { newRequestId } from '../../utils/requestId';
import {
  PencilSquareIcon,
  SparklesIcon,
  XMarkIcon,
  CheckIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const NoteQuickCaptureModal = ({ open, onClose }) => {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [processAI, setProcessAI] = useState(true);
  const capture = useRef(null);

  const contentMetrics = useMemo(() => {
    const words = content.trim().length === 0 ? 0 : content.trim().split(/\s+/).length;
    const characters = content.length;
    return { words, characters };
  }, [content]);

  const createMutation = useMutation({
    mutationFn: async () => {
      const values = { title, content, review_with_ai: processAI };
      const fingerprint = JSON.stringify(values);
      if (capture.current?.fingerprint !== fingerprint) capture.current = { fingerprint, id: newRequestId() };
      return api.post('/notes/', { ...values, request_id: capture.current.id }).then(r => r.data);
    },
    onSuccess: () => {
      toast.success('Note saved');
      queryClient.invalidateQueries({ queryKey: ['notes'] });
      queryClient.invalidateQueries({ queryKey: ['pending-notes-count'] });
      onClose?.();
      setTitle(''); setContent('');
      capture.current = null;
    },
    onError: (e) => toast.error(e.response?.data?.detail || 'Failed to save note')
  });

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-[#101813]/45 backdrop-blur-sm" onClick={createMutation.isPending ? undefined : onClose} />
      <div className="absolute left-0 right-0 bottom-0 px-4 pb-6">
        <div className="modal-safe animate-quick-sheet relative mx-auto max-w-screen-md overflow-hidden rounded-lg border border-[#dce3dd] bg-white shadow-[0_20px_60px_rgba(21,36,28,0.18)]">
          <div className="relative p-6">
            <div className="mx-auto mb-5 h-1 w-10 rounded-full bg-[#d9dfda]" />

            <header className="flex items-start justify-between text-[#17211c]">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#dcece4] text-[#246b52]">
                  <PencilSquareIcon className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-lg font-bold">Capture note</h2>
                  <p className="text-sm text-[#6d7871]">Save the thought now. Review commitments after.</p>
                </div>
              </div>
              <button
                onClick={onClose}
                disabled={createMutation.isPending}
                className="icon-button"
                aria-label="Close"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </header>

            <section className="mt-6 space-y-5">
              <div className="rounded-lg border border-[#dce3dd] bg-[#fbfcfb] p-4">
                <label htmlFor="capture-note-title" className="text-xs font-semibold uppercase tracking-wide text-gray-500">Note Title</label>
                <input
                  id="capture-note-title"
                  disabled={createMutation.isPending}
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="Brain dump, Meeting recap, Project idea…"
                  className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm font-medium text-gray-900 shadow-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-400/60"
                />
              </div>

              <div className="rounded-lg border border-[#dce3dd] bg-white p-4">
                <div className="flex items-center justify-between">
                  <label htmlFor="capture-note-body" className="text-xs font-semibold uppercase tracking-wide text-gray-500">Note Body</label>
                  <span className="flex items-center gap-2 text-xs text-gray-500">
                    <SparklesIcon className="h-4 w-4 text-purple-500" />
                    AI-ready
                  </span>
                </div>
                <textarea
                  id="capture-note-body"
                  maxLength={100000}
                  disabled={createMutation.isPending}
                  value={content}
                  onChange={e => setContent(e.target.value)}
                  placeholder="What’s on your mind?"
                  rows={5}
                  className="mt-3 w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm leading-5 text-gray-900 shadow-inner focus:border-purple-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-purple-400/50"
                />
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
                  <span>{contentMetrics.words} words • {contentMetrics.characters} characters</span>
                  <span className="text-gray-400">Tip: add bullet-style lines for richer AI insights.</span>
                </div>
              </div>

              <div className="flex items-start justify-between gap-3 rounded-lg border border-[#cfe0d6] bg-[#f4f8f5] px-4 py-3">
                <div className="flex items-center gap-3">
                  <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${processAI ? 'bg-white text-purple-600 shadow-sm' : 'bg-purple-100 text-purple-500'} transition`}>
                    <SparklesIcon className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-[#1e5844]">Review with AI</p>
                    <p className="text-xs text-[#597066]">Look for evidence-backed actions to review. Turning this off excludes the note from automatic AI scans.</p>
                  </div>
                </div>
                <button
                  aria-label="Review with AI"
                  aria-pressed={processAI}
                  disabled={createMutation.isPending}
                  onClick={() => setProcessAI(prev => !prev)}
                  className={`flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold transition ${
                    processAI ? 'bg-[#246b52] text-white' : 'bg-white text-[#246b52] ring-1 ring-[#bed9ca]'
                  }`}
                >
                  {processAI ? 'Enabled' : 'Enable'}
                </button>
              </div>
            </section>

            <footer className="mt-7 flex flex-col gap-3 border-t border-gray-100 pt-4 sm:flex-row sm:justify-end">
              <button
                disabled={createMutation.isPending}
                onClick={() => {
                  setTitle('');
                  setContent('');
                  onClose?.();
                }}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-600 transition hover:border-gray-300 hover:bg-gray-50"
              >
                Clear & Close
              </button>
              <button
                onClick={() => createMutation.mutate()}
                disabled={!content.trim() || createMutation.isPending}
                className="primary-button disabled:cursor-not-allowed disabled:opacity-60"
              >
                {createMutation.isPending ? (
                  <>
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                    <span>Saving…</span>
                  </>
                ) : (
                  <>
                    <CheckIcon className="h-4 w-4" />
                    <span>Save Note</span>
                  </>
                )}
              </button>
            </footer>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes quick-sheet {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .animate-quick-sheet {
          animation: quick-sheet 0.32s ease-out;
        }
      `}</style>
    </div>
  );
};

export default NoteQuickCaptureModal;

